import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../../hooks/use-toast';
import {
  ShieldAlert, Wifi, ScanFace, MapPinOff, CameraOff, ImageOff, MapPin,
  Camera, Check, RotateCcw, LogIn, LogOut, Loader2,
} from 'lucide-react';

import { ClockAlertEmailSwitches } from './ClockAlertEmailSwitches';

const API = process.env.REACT_APP_BACKEND_URL;
const TZ = 'Asia/Dubai';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(',', '');
  } catch { return iso; }
};

// Flag metadata: label, short tag, plain-language reason, severity, icon. Order = priority.
const FLAGS = [
  { key: 'flag_ip_rejected', label: 'Rejected IP', tag: 'Rejected IP', reason: 'Clocked in from an IP you rejected', sev: 'high', Icon: Wifi },
  { key: 'flag_ip_mismatch', label: 'Off-network (Wi-Fi/IP)', tag: 'Off-network', reason: 'Off-network — possible spoof', sev: 'high', Icon: Wifi },
  { key: 'flag_face_mismatch', label: 'Face mismatch', tag: 'Mismatch', reason: "Doesn't match DB photo", sev: 'high', Icon: ScanFace },
  { key: 'flag_face_unverified', label: 'Face unverified', tag: "Couldn't verify", reason: "Couldn't auto-verify face", sev: 'med', Icon: ScanFace },
  { key: 'flag_no_gps', label: 'No GPS', tag: 'No GPS', reason: 'Location not shared', sev: 'med', Icon: MapPinOff },
  { key: 'flag_geofence_outside', label: 'Outside geofence', tag: 'Outside geofence', reason: 'Outside the venue radius', sev: 'med', Icon: MapPinOff },
  { key: 'flag_no_selfie', label: 'No selfie', tag: 'No selfie', reason: 'No selfie taken', sev: 'med', Icon: CameraOff },
  { key: 'flag_no_geofence', label: 'Station not geo-pinned', tag: 'No geo-pin', reason: 'Station not geo-pinned', sev: 'low', Icon: MapPin },
  { key: 'flag_no_reference_photo', label: 'No profile photo', tag: 'No DB photo', reason: 'No DB photo to compare', sev: 'low', Icon: ImageOff },
];

// The single, most-important reason this punch needs a look (highest-priority active flag).
const reviewReason = (e) => {
  const f = FLAGS.find(fl => e[fl.key]);
  return f ? { text: f.reason, sev: f.sev, Icon: f.Icon } : { text: 'Flagged', sev: 'low', Icon: ShieldAlert };
};

const SEV_CARD = {
  high: 'from-rose-50 to-red-50 border-rose-200 text-rose-700',
  med: 'from-amber-50 to-orange-50 border-amber-200 text-amber-700',
  low: 'from-slate-50 to-gray-50 border-gray-200 text-gray-600',
};
const SEV_BADGE = {
  high: 'text-rose-700 border-rose-300 bg-rose-50',
  med: 'text-amber-700 border-amber-300 bg-amber-50',
  low: 'text-gray-600 border-gray-300 bg-gray-50',
};

const rowSeverity = (e) => {
  if (e.flag_ip_rejected || e.flag_ip_mismatch || e.flag_face_mismatch) return 'high';
  if (e.flag_no_gps || e.flag_no_selfie || e.flag_geofence_outside || e.flag_face_unverified) return 'med';
  return 'low';
};

const FlaggedTab = () => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 10);
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);

  const [form, setForm] = useState({ date_from: weekAgo, date_to: today });
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(null);

  const [selfieEvent, setSelfieEvent] = useState(null);
  const [selfieBlobUrl, setSelfieBlobUrl] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/api/clock/flagged`, {
      headers,
      params: { ...form, include_reviewed: includeReviewed },
    })
      .then(r => setData(r.data))
      .catch(() => toast({ title: 'Failed to load flagged punches', variant: 'destructive' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, includeReviewed]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selfieEvent) {
      if (selfieBlobUrl) URL.revokeObjectURL(selfieBlobUrl);
      setSelfieBlobUrl(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API}/api/clock/admin/selfie/${selfieEvent.id}`, { headers, responseType: 'blob' })
      .then(r => { if (!cancelled) setSelfieBlobUrl(URL.createObjectURL(r.data)); })
      .catch(() => { if (!cancelled) setSelfieBlobUrl('error'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfieEvent]);

  const review = async (e, reviewed) => {
    setBusyId(e.id);
    try {
      await axios.post(`${API}/api/clock/flagged/${e.id}/review`, { reviewed }, { headers });
      toast({ title: reviewed ? 'Marked as reviewed' : 'Re-opened' });
      load();
    } catch {
      toast({ title: 'Action failed', variant: 'destructive' });
    } finally { setBusyId(null); }
  };

  const bulkReview = async (level, count) => {
    const label = { high: 'High', med: 'Medium', low: 'Low', all: 'flagged' }[level];
    if (!window.confirm(`Mark all ${count} unreviewed ${label} punch(es) in this date range as reviewed?`)) return;
    setBulkBusy(level);
    try {
      const r = await axios.post(`${API}/api/clock/flagged/review-bulk`,
        { level, date_from: form.date_from, date_to: form.date_to }, { headers });
      toast({ title: `${r.data.reviewed_count} punch(es) marked reviewed` });
      load();
    } catch {
      toast({ title: 'Bulk review failed', variant: 'destructive' });
    } finally { setBulkBusy(null); }
  };

  const bulkReviewFlag = async (flag, label, count) => {
    if (!window.confirm(`Mark all ${count} unreviewed ${label} punch(es) in this date range as reviewed?`)) return;
    setBulkBusy(flag);
    try {
      const r = await axios.post(`${API}/api/clock/flagged/review-bulk`,
        { flag, date_from: form.date_from, date_to: form.date_to }, { headers });
      toast({ title: `${r.data.reviewed_count} ${label} punch(es) marked reviewed` });
      load();
    } catch {
      toast({ title: 'Bulk review failed', variant: 'destructive' });
    } finally { setBulkBusy(null); }
  };

  const events = data?.events || [];
  const summary = data?.summary || {};

  // Per-level unreviewed counts (drive the bulk-review buttons).
  const unreviewedEvents = events.filter(e => !e.flag_reviewed);
  const levelCounts = { high: 0, med: 0, low: 0 };
  unreviewedEvents.forEach(e => { levelCounts[rowSeverity(e)] += 1; });
  const totalUnreviewed = unreviewedEvents.length;
  const noGpsUnreviewed = unreviewedEvents.filter(e => e.flag_no_gps).length;

  return (
    <div className="space-y-3" data-testid="flagged-tab">
      <ClockAlertEmailSwitches compact />
      {/* Filters */}
      <Card><CardContent className="p-3 flex flex-wrap gap-3 items-end">
        <div><Label className="text-xs">From</Label>
          <Input type="date" value={form.date_from}
            onChange={e => setForm(f => ({ ...f, date_from: e.target.value }))}
            data-testid="flagged-date-from" /></div>
        <div><Label className="text-xs">To</Label>
          <Input type="date" value={form.date_to}
            onChange={e => setForm(f => ({ ...f, date_to: e.target.value }))}
            data-testid="flagged-date-to" /></div>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer pb-1">
          <input type="checkbox" checked={includeReviewed}
            onChange={e => setIncludeReviewed(e.target.checked)}
            data-testid="flagged-include-reviewed" />
          Show reviewed too
        </label>
        <div className="ml-auto text-xs text-gray-500 pb-1">
          {data && (
            <span data-testid="flagged-counts">
              <b className="text-rose-600">{data.unreviewed}</b> unreviewed · {data.total_flagged} total flagged
            </span>
          )}
        </div>
      </CardContent></Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {FLAGS.map(f => {
          const Icon = f.Icon;
          const count = summary[f.key] || 0;
          return (
            <Card key={f.key} className={`bg-gradient-to-br ${SEV_CARD[f.sev]} border`}>
              <CardContent className="p-3">
                <p className="text-[10px] uppercase font-semibold flex items-center gap-1">
                  <Icon className="h-3.5 w-3.5" /> {f.tag}
                </p>
                <p className="text-2xl font-bold mt-1" data-testid={`flagged-summary-${f.key}`}>{count}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Bulk review by severity level */}
      {totalUnreviewed > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-1" data-testid="flagged-bulk-bar">
          <span className="text-xs font-medium text-gray-500 mr-1">Review all by level:</span>
          {noGpsUnreviewed > 0 && (
            <Button size="sm" variant="outline" disabled={bulkBusy === 'flag_no_gps'}
              className="h-7 px-2 text-[11px] text-amber-700 border-amber-300 hover:bg-amber-50"
              onClick={() => bulkReviewFlag('flag_no_gps', 'No-GPS', noGpsUnreviewed)} data-testid="flagged-bulk-nogps">
              {bulkBusy === 'flag_no_gps' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <MapPinOff className="h-3 w-3 mr-1" />}
              No-GPS ({noGpsUnreviewed})
            </Button>
          )}
          {levelCounts.high > 0 && (
            <Button size="sm" variant="outline" disabled={bulkBusy === 'high'}
              className="h-7 px-2 text-[11px] text-rose-700 border-rose-300 hover:bg-rose-50"
              onClick={() => bulkReview('high', levelCounts.high)} data-testid="flagged-bulk-high">
              {bulkBusy === 'high' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
              High ({levelCounts.high})
            </Button>
          )}
          {levelCounts.med > 0 && (
            <Button size="sm" variant="outline" disabled={bulkBusy === 'med'}
              className="h-7 px-2 text-[11px] text-amber-700 border-amber-300 hover:bg-amber-50"
              onClick={() => bulkReview('med', levelCounts.med)} data-testid="flagged-bulk-med">
              {bulkBusy === 'med' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
              Medium ({levelCounts.med})
            </Button>
          )}
          {levelCounts.low > 0 && (
            <Button size="sm" variant="outline" disabled={bulkBusy === 'low'}
              className="h-7 px-2 text-[11px] text-gray-600 border-gray-300 hover:bg-gray-50"
              onClick={() => bulkReview('low', levelCounts.low)} data-testid="flagged-bulk-low">
              {bulkBusy === 'low' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
              Low ({levelCounts.low})
            </Button>
          )}
          <Button size="sm" disabled={bulkBusy === 'all'}
            className="h-7 px-2 text-[11px] bg-emerald-600 hover:bg-emerald-700 ml-auto"
            onClick={() => bulkReview('all', totalUnreviewed)} data-testid="flagged-bulk-all">
            {bulkBusy === 'all' ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
            Review all ({totalUnreviewed})
          </Button>
        </div>
      )}

      {/* Table */}
      <Card><CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : events.length === 0 ? (
          <div className="p-8 text-center text-emerald-600 flex flex-col items-center gap-1" data-testid="flagged-empty">
            <ShieldAlert className="h-8 w-8 text-emerald-400" />
            <p className="font-medium">No flagged punches in this range</p>
            <p className="text-xs text-gray-400">Off-network / spoofing attempts will show up here.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="text-left p-3">Severity</th>
                <th className="text-left p-3">Time</th>
                <th className="text-left p-3">Employee</th>
                <th className="text-left p-3">Station</th>
                <th className="text-left p-3">Why flagged</th>
                <th className="text-left p-3">Network IP</th>
                <th className="text-right p-3">Review</th>
              </tr>
            </thead>
            <tbody>
              {events.map(e => {
                const sev = rowSeverity(e);
                const activeFlags = FLAGS.filter(f => e[f.key]);
                return (
                  <tr key={e.id}
                    className={`border-t hover:bg-gray-50 ${e.flag_reviewed ? 'opacity-60' : ''}`}
                    data-testid={`flagged-row-${e.id}`}>
                    <td className="p-3">
                      <Badge variant="outline" className={`text-[10px] ${SEV_BADGE[sev]}`}>
                        {sev === 'high' ? 'High' : sev === 'med' ? 'Medium' : 'Low'}
                      </Badge>
                    </td>
                    <td className="p-3 font-mono text-xs whitespace-nowrap">
                      {fmtDateTime(e.timestamp)}
                      <span className="ml-1">
                        {e.type === 'in'
                          ? <LogIn className="h-3 w-3 inline text-emerald-600" />
                          : <LogOut className="h-3 w-3 inline text-red-600" />}
                      </span>
                    </td>
                    <td className="p-3">{e.employee_name}
                      <span className="text-xs text-gray-400"> · {e.employee_code}</span></td>
                    <td className="p-3 text-xs">{e.branch_name || '—'}
                      {e.distance_m != null && <span className="text-gray-400"> · {Math.round(e.distance_m)}m</span>}
                    </td>
                    <td className="p-3">
                      {(() => { const r = reviewReason(e); const RIcon = r.Icon; return (
                        <div className="flex items-start gap-1.5">
                          <RIcon className={`h-4 w-4 mt-0.5 shrink-0 ${r.sev === 'high' ? 'text-rose-600' : r.sev === 'med' ? 'text-amber-600' : 'text-gray-500'}`} />
                          <div>
                            <p className={`text-xs font-semibold ${r.sev === 'high' ? 'text-rose-700' : r.sev === 'med' ? 'text-amber-700' : 'text-gray-600'}`} data-testid={`flagged-reason-${e.id}`}>
                              {r.text}
                            </p>
                            {activeFlags.length > 1 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {activeFlags.map(f => (
                                  <Badge key={f.key} variant="outline" className={`text-[9px] ${SEV_BADGE[f.sev]}`}>
                                    {f.tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ); })()}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-gray-600">
                      {e.client_ip || '—'}
                      {e.selfie_path && (
                        <button onClick={() => setSelfieEvent(e)}
                          className="ml-2 inline-flex items-center gap-0.5 text-[10px] text-violet-600 underline"
                          data-testid={`flagged-selfie-${e.id}`}>
                          <Camera className="h-3 w-3" /> selfie
                        </button>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {e.flag_reviewed ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                            <Check className="h-3 w-3 mr-1" /> Reviewed
                          </Badge>
                          <button onClick={() => review(e, false)} disabled={busyId === e.id}
                            className="text-[10px] text-gray-400 hover:text-gray-600 inline-flex items-center gap-0.5"
                            data-testid={`flagged-reopen-${e.id}`}>
                            <RotateCcw className="h-3 w-3" /> reopen
                          </button>
                          {e.flag_reviewed_by && (
                            <span className="text-[9px] text-gray-400">by {e.flag_reviewed_by}</span>
                          )}
                        </div>
                      ) : (
                        <Button size="sm" variant="outline"
                          className="h-7 px-2 text-[11px] text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          disabled={busyId === e.id}
                          onClick={() => review(e, true)}
                          data-testid={`flagged-review-${e.id}`}>
                          {busyId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 mr-1" />}
                          Mark reviewed
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardContent></Card>

      <p className="text-[11px] text-gray-400 px-1">
        Punches are flagged automatically. <b>Off-network</b> means the clock-in did not come from the station's
        trusted Wi-Fi/IP — the strongest indicator of GPS spoofing. Selfies are kept for 7 days.
      </p>

      {/* Selfie viewer */}
      <Dialog open={!!selfieEvent} onOpenChange={(o) => { if (!o) setSelfieEvent(null); }}>
        <DialogContent className="max-w-lg" data-testid="flagged-selfie-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-violet-600" />
              Selfie — {selfieEvent?.employee_name}
            </DialogTitle>
          </DialogHeader>
          {selfieEvent && (
            <div className="space-y-3">
              {(() => { const r = reviewReason(selfieEvent); const RIcon = r.Icon; return (
                <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold border ${SEV_BADGE[r.sev]}`} data-testid="flagged-selfie-reason">
                  <RIcon className="h-4 w-4" /> Why review: {r.text}
                </div>
              ); })()}
              <div className="bg-gray-50 rounded p-3 text-xs space-y-1">
                <p><span className="text-gray-500">Time:</span> {fmtDateTime(selfieEvent.timestamp)}</p>
                <p><span className="text-gray-500">Station:</span> {selfieEvent.branch_name || '—'}</p>
                <p><span className="text-gray-500">Network IP:</span> <span className="font-mono">{selfieEvent.client_ip || '—'}</span></p>
                {selfieEvent.face_match_score != null && (
                  <p><span className="text-gray-500">Face match:</span>{' '}
                    <span className={selfieEvent.flag_face_mismatch ? 'text-rose-600 font-bold' : 'text-emerald-600 font-bold'}>
                      {Math.round(selfieEvent.face_match_score * 100)}%{selfieEvent.flag_face_mismatch ? ' — mismatch' : ''}
                    </span>
                  </p>
                )}
              </div>
              <div className="text-center">
                {!selfieBlobUrl && <div className="aspect-square bg-gray-100 rounded flex items-center justify-center text-xs text-gray-400">Loading…</div>}
                {selfieBlobUrl === 'error' && <div className="aspect-square bg-rose-50 rounded flex items-center justify-center text-xs text-rose-600 p-3 text-center">Selfie expired or unavailable (7-day retention)</div>}
                {selfieBlobUrl && selfieBlobUrl !== 'error' && (
                  <img src={selfieBlobUrl} alt="Selfie" className="max-h-[400px] mx-auto object-contain rounded" data-testid="flagged-selfie-image" />
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlaggedTab;
